import { Injectable, OnDestroy, signal, inject, PLATFORM_ID, effect, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Auth } from '../auth/auth';
import { WebSocketService, ChatMessage as WSChatMessage } from '../websocket/websocket.service';
import { SiteStatusService } from '../site-status.service';
import { environment } from '../../../environments/environment';

export interface ChatMessage {
  id: number;
  conversation_id?: string;
  sender_id: number;
  receiver_id: number;
  sender_name?: string;
  sender_avatar?: string | null;
  content: string;
  timestamp: Date;
  is_read: boolean;
  read_by_count?: number;
  message_type: 'text' | 'image' | 'file' | 'embed';
  embed?: any;
}

export interface ChatConversation {
  id: string;
  is_group?: boolean;
  name?: string;
  participant_ids?: string[];
  participant_count?: number;
  participants?: Array<{
    id: number;
    display_name: string;
    avatar?: string | null;
  }>;
  participant_id: string;
  participant_name: string;
  participant_avatar?: string;
  last_message?: ChatMessage;
  unread_count: number;
  status: 'online' | 'away' | 'offline' | 'busy' | 'group';
  last_seen?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService implements OnDestroy {
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private wsService = inject(WebSocketService);
  private siteStatus = inject(SiteStatusService);
  private platformId = inject(PLATFORM_ID);
  private refreshInterval: any = null;
  
  // Signals for reactive state management
  conversations = signal<ChatConversation[]>([]);
  isChatOpen = signal(false);
  selectedConversation = signal<ChatConversation | null>(null);
  messages = signal<ChatMessage[]>([]);
  temporaryConversation = signal<ChatConversation | null>(null);
  private selectingConversationId: string | null = null;
  private seenMessageIds = new Set<string>();
  private readonly maxSeenMessageIds = 2000;
  private readonly boundChatMessageListener = (event: Event) => {
    const message = (event as CustomEvent).detail as WSChatMessage;
    this.handleIncomingMessage(message);
  };
  private readonly boundNewConversationListener = (event: Event) => {
    const conversation = (event as CustomEvent).detail;
    this.handleNewConversation(conversation);
  };
  private readonly boundConversationUpdatedListener = (event: Event) => {
    const conversation = (event as CustomEvent).detail;
    this.handleConversationUpdate(conversation);
  };
  private readonly boundWebsocketConnectedListener = () => {
    this.loadRecentConversations();
    this.stopFallbackPolling();
  };
  private readonly boundWebsocketDisconnectedListener = () => {
    if (this.isOnAuthOrSharePage()) return;
    this.startFallbackPolling();
  };
  
  // Separate state for messages app vs chat bubble
  isInMessagesApp = signal(false);
  chatBubbleEnabled = signal(true);

  /** Guards the messages-enabled-transitioned-to-true effect below so a
   * status refresh that leaves the flag unchanged doesn't re-fetch. */
  private conversationsLoadedForFlag = false;

  constructor() {
    this.initializeChatBubble();
    this.setupWebSocketListeners();
    this.setupStatusSync();
    // Messages is a feature-flagged (default OFF) surface — loading
    // conversations always 403s while it's off (quality-sweep item 11:
    // background console noise on every page). Site status loads
    // asynchronously, so this effect (not a direct constructor call) is the
    // single load trigger: it's a no-op until the flag is confirmed ON, then
    // fires exactly once. The guard inside loadRecentConversations() itself
    // covers every OTHER call site (websocket reconnect, post-send, etc.).
    effect(() => {
      const enabled = this.siteStatus.loaded() && this.siteStatus.messagesEnabled();
      if (enabled && !this.conversationsLoadedForFlag && !this.isOnAuthOrSharePage()) {
        this.conversationsLoadedForFlag = true;
        untracked(() => this.loadRecentConversations());
      }
    });
    // Removed polling - now relying on WebSocket events
  }

  private setupWebSocketListeners(): void {
    // Only setup listeners in browser environment
    if (!isPlatformBrowser(this.platformId)) return;
    
    // Listen for incoming chat messages
    window.addEventListener('chatMessage', this.boundChatMessageListener);
    
    // Listen for new conversations
    window.addEventListener('newConversation', this.boundNewConversationListener);
    
    // Listen for conversation updates
    window.addEventListener('conversationUpdated', this.boundConversationUpdatedListener);
    
    // Listen for WebSocket connection status changes
    window.addEventListener('websocketConnected', this.boundWebsocketConnectedListener);
    
    window.addEventListener('websocketDisconnected', this.boundWebsocketDisconnectedListener);
  }

  private setupStatusSync(): void {
    // Keep conversation status in sync with status WebSocket updates
    effect(() => {
      const statuses = this.wsService.userStatuses();
      untracked(() => {
        this.conversations.update(conversations =>
          this.sanitizeConversations(conversations).map(conv => this.applyStatusToConversation(conv, statuses))
        );
        const current = this.selectedConversation();
        if (current) {
          const next = this.applyStatusToConversation(current, statuses);
          if (next.status !== current.status) {
            this.selectedConversation.set(next);
          }
        }
      });
    });
  }

  private applyStatusToConversation(conversation: ChatConversation, statuses: any[]): ChatConversation {
    if (conversation.is_group) {
      return { ...conversation, status: 'group' };
    }
    const participantId = Number(conversation.participant_id);
    const status = this.wsService.getProfileStatus(participantId);
    return { ...conversation, status };
  }

  private handleIncomingMessage(wsMessage: WSChatMessage): void {
    const selectedProfile = this.auth.getSelectedProfile();
    if (!selectedProfile) return;

    // Convert WebSocket message to our ChatMessage format
    const messageConversationId = wsMessage.conversation_id ? String(wsMessage.conversation_id) : undefined;
    const messageId = String(wsMessage.id);
    if (this.seenMessageIds.has(messageId)) {
      return;
    }
    this.seenMessageIds.add(messageId);
    this.pruneSeenMessageIds();

    const message: ChatMessage = {
      id: parseInt(wsMessage.id),
      conversation_id: messageConversationId,
      sender_id: parseInt(wsMessage.sender_id),
      receiver_id: wsMessage.receiver_id ? parseInt(wsMessage.receiver_id) : selectedProfile.id,
      sender_name: wsMessage.sender_name,
      sender_avatar: wsMessage.sender_avatar ?? null,
      content: wsMessage.content,
      timestamp: new Date(wsMessage.timestamp),
      is_read: wsMessage.is_read,
      read_by_count: wsMessage.read_by_count || 0,
      message_type: wsMessage.message_type as 'text' | 'image' | 'file',
      embed: wsMessage.embed
    };

    // Add to current messages if it's for the selected conversation
    const currentConv = this.selectedConversation();
    const resolvedConversationId = messageConversationId || currentConv?.id;
    if (currentConv && resolvedConversationId === currentConv.id) {
      this.messages.update(messages => [...messages, message]);
      this.markMessagesAsRead(currentConv.id);
    }

    // Update conversation's last message; increment unread if not viewing this conversation
    if (resolvedConversationId) {
      const isViewingConversation = currentConv?.id === resolvedConversationId;
      this.conversations.update(conversations =>
        conversations.map(conv =>
          conv.id === resolvedConversationId
            ? {
                ...conv,
                last_message: message,
                unread_count: isViewingConversation ? conv.unread_count : (conv.unread_count || 0) + 1
              }
            : conv
        )
      );
      // Notify sidebar to refresh message count when new unread arrives
      if (!isViewingConversation && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('notificationCountsChanged'));
      }
    }
  }

  private handleNewConversation(conversation: any): void {
    console.log('ChatService: Handling new conversation', conversation);

    // Add the new conversation to the list if it doesn't already exist
    this.conversations.update(conversations => {
      const normalized = this.normalizeConversation(conversation);
      const exists = conversations.some(conv => conv.id === normalized?.id);
      if (!exists) {
        console.log('ChatService: Adding new conversation to list');
        return this.sanitizeConversations([...conversations, normalized]);
      }
      return this.sanitizeConversations(conversations);
    });
  }

  private handleConversationUpdate(conversation: any): void {
    const selectedProfile = this.auth.getSelectedProfile();
    if (!selectedProfile) return;

    console.log('ChatService: Handling conversation update', conversation);

    // Update the conversation in the list
    this.conversations.update(conversations => {
      const normalized = this.normalizeConversation(conversation);
      return this.sanitizeConversations(conversations).map(conv =>
        conv.id === normalized?.id ? { ...conv, ...normalized } : conv
      );
    });
    // Notify sidebar to refresh message count when conversation unread changes
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('notificationCountsChanged'));
    }
  }

  private isOnAuthOrSharePage(): boolean {
    if (typeof window === 'undefined') return false;
    const path = window.location?.pathname || '';
    return path.startsWith('/share/') || path.startsWith('/auth/');
  }

  async loadRecentConversations(): Promise<void> {
    // Skip API calls during SSR
    if (!isPlatformBrowser(this.platformId)) {
      this.conversations.set([]);
      return;
    }
    // Skip on auth pages (login, signup, forgot, reset) and share pages - user not logged in, would get 401 and trigger redirect
    if (this.isOnAuthOrSharePage()) {
      this.conversations.set([]);
      return;
    }
    // Messages is feature-flagged (default OFF) — calling this while off
    // always 403s (quality-sweep item 11: background console noise on
    // every page). Skip the request entirely until the flag is confirmed on.
    if (!this.siteStatus.messagesEnabled()) {
      this.conversations.set([]);
      return;
    }

    console.log('ChatService: Loading recent conversations...');
    try {
      const selectedProfile = this.auth.getSelectedProfile();
      const profileId = selectedProfile?.id;
      console.log('ChatService: Using profile ID:', profileId);

      const data: any = await firstValueFrom(
        this.http.get(`${environment.apiBaseUrl}/chat/conversations/`, {
          params: { profile_id: String(profileId ?? '') }
        })
      );

      console.log('Loaded conversations:', data);
      // Transform the data to match frontend expectations
      const conversations = this.sanitizeConversations(
        (data?.results || data || []).map((conv: any) => this.normalizeConversation(conv))
      );

      this.conversations.set(conversations);
      // Notify sidebar to refresh message count
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('notificationCountsChanged'));
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      this.conversations.set([]);
    }
  }

  async loadMessages(conversationId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      this.messages.set([]);
      return;
    }

    console.log(`ChatService: Loading messages for conversation ${conversationId}`);
    try {
      const selectedProfile = this.auth.getSelectedProfile();
      const profileId = selectedProfile?.id;
      console.log('ChatService: Loading messages with profile ID:', profileId);

      const data: any = await firstValueFrom(
        this.http.get(`${environment.apiBaseUrl}/chat/conversations/${conversationId}/messages/`, {
          params: { profile_id: String(profileId ?? ''), limit: '200' }
        })
      );

      console.log('Loaded messages:', data);
      const messages: ChatMessage[] = (data?.results || data || []).map((msg: any) => ({
        id: msg.id,
        conversation_id: msg.conversation_id || conversationId,
        sender_id: msg.sender,
        receiver_id: msg.receiver ?? msg.sender,
        sender_name: msg.sender_name,
        sender_avatar: msg.sender_avatar ?? null,
        content: msg.content,
        timestamp: new Date(msg.timestamp || msg.created_at),
        is_read: msg.is_read,
        read_by_count: msg.read_by_count || 0,
        message_type: msg.message_type || 'text',
        embed: msg.embed
      }));
      this.messages.set(messages);
      messages.forEach(message => this.seenMessageIds.add(String(message.id)));
    } catch (error) {
      console.error('Failed to load messages:', error);
      this.messages.set([]);
    }
  }

  async sendMessage(content: string, receiverId: string, embed?: any): Promise<void> {
    try {
      const selectedProfile = this.auth.getSelectedProfile();
      if (!selectedProfile) {
        console.error('No selected profile for sending message');
        return;
      }

      let conversation = this.selectedConversation();
      
      // If we have a temporary conversation, create the actual conversation first
      if (!conversation || conversation.id === 'temp') {
        const tempConv = this.temporaryConversation();
        if (tempConv) {
          console.log('Creating conversation for first message');
          await this.createConversationAndSendMessage(tempConv, content, embed);
          return;
        }
      }

      if (!conversation) {
        console.error('No selected conversation for sending message');
        return;
      }

      // Send via REST API for persistence
      const newMessage: any = await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/chat/conversations/${conversation.id}/messages/`, {
          content: content,
          message_type: embed ? 'embed' : 'text',
          embed: embed || null
        })
      );
      console.log('Message sent successfully:', newMessage);
        
      const messageId = String(newMessage.id);
      if (this.seenMessageIds.has(messageId)) {
        return;
      }
      this.seenMessageIds.add(messageId);
      this.pruneSeenMessageIds();

      // Add the new message to the current messages list
      const message: ChatMessage = {
        id: newMessage.id,
        conversation_id: newMessage.conversation_id || conversation.id,
        sender_id: newMessage.sender,
        receiver_id: newMessage.receiver,
        sender_name: newMessage.sender_name,
        sender_avatar: newMessage.sender_avatar ?? null,
        content: content,
        timestamp: new Date(newMessage.timestamp || newMessage.created_at),
        is_read: false,
        read_by_count: 0,
        message_type: embed ? 'embed' : 'text',
        embed: embed || null
      };
      this.messages.update(messages => [...messages, message]);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }

  openChat(): void {
    // Don't open chat bubble if user is in messages app
    if (this.isInMessagesApp()) {
      return;
    }
    this.isChatOpen.set(true);
  }

  closeChat(): void {
    this.isChatOpen.set(false);
    this.selectedConversation.set(null);
  }

  // Methods to manage messages app state
  setMessagesAppActive(isActive: boolean): void {
    if (isActive) {
      this.enterMessagesApp();
    } else {
      this.exitMessagesApp();
    }
  }

  enterMessagesApp(): void {
    console.log('ChatService: enterMessagesApp called');
    this.isInMessagesApp.set(true);
    this.chatBubbleEnabled.set(false);
    this.closeChat(); // Close chat bubble when entering messages app
    console.log('ChatService: after enterMessagesApp - isInMessagesApp:', this.isInMessagesApp(), ', chatBubbleEnabled:', this.chatBubbleEnabled());
  }

  exitMessagesApp(): void {
    console.log('ChatService: exitMessagesApp called');
    this.isInMessagesApp.set(false);
    this.chatBubbleEnabled.set(true);
    console.log('ChatService: after exitMessagesApp - isInMessagesApp:', this.isInMessagesApp(), ', chatBubbleEnabled:', this.chatBubbleEnabled());
  }

  // Check if chat bubble should be visible
  shouldShowChatBubble(): boolean {
    const result = this.chatBubbleEnabled() && !this.isInMessagesApp();
    console.log('ChatService: shouldShowChatBubble =', result, '(chatBubbleEnabled:', this.chatBubbleEnabled(), ', isInMessagesApp:', this.isInMessagesApp(), ')');
    return result;
  }

  // Initialize chat bubble state
  initializeChatBubble(): void {
    this.chatBubbleEnabled.set(true);
    this.isInMessagesApp.set(false);
  }

  selectConversation(conversation: ChatConversation): void {
    if (!conversation || !conversation.id) {
      console.warn('ChatService: Ignoring invalid conversation', conversation);
      return;
    }
    const current = this.selectedConversation();
    if (current?.id === conversation.id) {
      return;
    }
    if (this.selectingConversationId === conversation.id) {
      return;
    }
    this.selectingConversationId = conversation.id;

    // Clear stale messages before loading the next thread
    this.messages.set([]);
    this.selectedConversation.set(conversation);
    queueMicrotask(() => {
      this.loadMessages(conversation.id);
      
      // Mark messages as read when selecting conversation
      this.markMessagesAsRead(conversation.id);
      
      // Connect to WebSocket for this conversation
      if (this.wsService) {
        this.wsService.connectChatSocket(conversation.id);
      }
      this.selectingConversationId = null;
    });
  }

  getTotalUnreadCount(): number {
    return this.conversations().reduce((total, conv) => total + conv.unread_count, 0);
  }

  async markMessagesAsRead(conversationId: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/chat/conversations/${conversationId}/messages/mark-read/`, {})
      );
      console.log('Messages marked as read successfully');
      this.conversations.update(conversations => 
        conversations.map(conv => 
          conv.id === conversationId 
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('notificationCountsChanged'));
      }
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  }

  async startConversation(participantId: string): Promise<void> {
    try {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      const data: any = await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/chat/conversations/start_conversation/`, {
          other_profile_id: participantId
        })
      );
      console.log('Conversation started successfully:', data);
      const conversation = this.normalizeConversation(data);
      this.conversations.update(conversations => this.sanitizeConversations([...conversations, conversation]));
      this.selectConversation(conversation);
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  }

  async startGroupConversation(participantIds: string[], name?: string): Promise<void> {
    try {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      const data: any = await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/chat/conversations/start_conversation/`, {
          participant_ids: participantIds,
          name: name || null
        })
      );
      console.log('Group conversation started successfully:', data);
      const conversation = this.normalizeConversation(data);
      this.conversations.update(conversations => this.sanitizeConversations([...conversations, conversation]));
      this.selectConversation(conversation);
    } catch (error) {
      console.error('Failed to start group conversation:', error);
    }
  }

  // Fallback polling methods for when WebSocket is unavailable
  private fallbackInterval: any = null;
  
  private startFallbackPolling(): void {
    // Only start fallback polling in browser when not on auth/share pages
    if (typeof window === 'undefined' || this.isOnAuthOrSharePage()) return;
    
    // Stop any existing fallback polling
    this.stopFallbackPolling();
    
    // Start fallback polling every 10 seconds when WebSocket is down
    this.fallbackInterval = setInterval(() => {
      if (this.isOnAuthOrSharePage()) return;
      console.log('ChatService: Fallback polling - refreshing conversations');
      this.loadRecentConversations();
    }, 10000); // 10 seconds
  }
  
  private stopFallbackPolling(): void {
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval);
      this.fallbackInterval = null;
      console.log('ChatService: Stopped fallback polling');
    }
  }

  setupTemporaryConversation(member: any): void {
    // Create a temporary conversation for the UI
    const tempConversation: ChatConversation = {
      id: 'temp',
      is_group: false,
      participant_id: member.id.toString(),
      participant_name: member.display_name || member.name,
      participant_avatar: member.avatar,
      last_message: undefined,
      unread_count: 0,
      status: 'offline' // Will be updated by status service
    };
    
    this.temporaryConversation.set(tempConversation);
    this.selectedConversation.set(tempConversation);
    this.messages.set([]); // Clear messages for new conversation
  }

  private async createConversationAndSendMessage(tempConv: ChatConversation, content: string, embed?: any): Promise<void> {
    try {
      const data: any = await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/chat/conversations/start_conversation/`, {
          other_profile_id: tempConv.participant_id
        })
      );
      console.log('Conversation created successfully:', data);

      const conversation = this.normalizeConversation(data);

      this.temporaryConversation.set(null);
      this.selectedConversation.set(conversation);
      this.conversations.update(conversations => [...conversations, conversation]);

      await this.sendMessageToConversation(conversation.id, content, embed);

      if (this.wsService) {
        this.wsService.connectChatSocket(conversation.id);
      }
    } catch (error) {
      console.error('Failed to create conversation and send message:', error);
    }
  }

  private async sendMessageToConversation(conversationId: string, content: string, embed?: any): Promise<void> {
    try {
      const newMessage: any = await firstValueFrom(
        this.http.post(`${environment.apiBaseUrl}/chat/conversations/${conversationId}/messages/`, {
          content: content,
          message_type: embed ? 'embed' : 'text',
          embed: embed || null
        })
      );
      console.log('First message sent successfully:', newMessage);

      const messageId = String(newMessage.id);
      if (this.seenMessageIds.has(messageId)) {
        return;
      }
      this.seenMessageIds.add(messageId);
      this.pruneSeenMessageIds();

      const message: ChatMessage = {
        id: newMessage.id,
        conversation_id: newMessage.conversation_id || conversationId,
        sender_id: newMessage.sender,
        receiver_id: newMessage.receiver,
        sender_name: newMessage.sender_name,
        sender_avatar: newMessage.sender_avatar ?? null,
        content: content,
        timestamp: new Date(newMessage.timestamp || newMessage.created_at),
        is_read: false,
        read_by_count: 0,
        message_type: 'text'
      };
      this.messages.update(messages => [...messages, message]);
    } catch (error) {
      console.error('Failed to send first message:', error);
    }
  }

  /** Angular calls this automatically when the providing injector is torn
   * down (route/lazy-module destroy in prod; between-test TestBed reset in
   * specs). Previously named `destroy()`, which nothing ever called —
   * `fallbackInterval` and the window listeners outlived every spec that
   * triggered them, and the interval's own `console.log` (or a
   * WebSocketService reconnect timer's) could fire after Vitest started
   * tearing down a *later* test file's environment, surfacing as
   * `EnvironmentTeardownError: ... "onUserConsoleLog" was pending` against
   * whatever spec happened to be running at that moment. */
  ngOnDestroy(): void {
    // Clean up intervals
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('chatMessage', this.boundChatMessageListener);
      window.removeEventListener('newConversation', this.boundNewConversationListener);
      window.removeEventListener('conversationUpdated', this.boundConversationUpdatedListener);
      window.removeEventListener('websocketConnected', this.boundWebsocketConnectedListener);
      window.removeEventListener('websocketDisconnected', this.boundWebsocketDisconnectedListener);
    }
    this.stopFallbackPolling();
  }

  private pruneSeenMessageIds(): void {
    if (this.seenMessageIds.size <= this.maxSeenMessageIds) {
      return;
    }
    const ids = Array.from(this.seenMessageIds);
    const keep = ids.slice(ids.length - this.maxSeenMessageIds);
    this.seenMessageIds = new Set(keep);
  }

  private normalizeConversation(conv: any): ChatConversation {
    if (!conv || !conv.id) {
      return conv;
    }
    const isGroup = !!conv.is_group;
    if (isGroup) {
      const groupName = conv.name || (conv.participants || []).map((p: any) => p.display_name).join(', ');
      return {
        ...conv,
        id: String(conv.id),
        is_group: true,
        name: conv.name || '',
        participant_id: conv.participant_id || '',
        participant_name: groupName || 'Group chat',
        participant_avatar: null,
        status: 'group',
        unread_count: conv.unread_count || 0
      };
    }

    if (conv.other_participant) {
      return {
        ...conv,
        id: String(conv.id),
        participant_id: conv.other_participant.id.toString(),
        participant_name: conv.other_participant.display_name,
        participant_avatar: conv.other_participant.avatar,
        status: conv.other_participant.status
      };
    }

    return {
      ...conv,
      id: String(conv.id),
      participant_id: conv.participant_id ?? '',
      participant_name: conv.participant_name ?? '',
      participant_avatar: conv.participant_avatar,
      status: conv.status || 'offline'
    };
  }

  private sanitizeConversations(conversations: any[]): ChatConversation[] {
    return (conversations || []).filter((conv: any) => conv && conv.id);
  }
}
